import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { setAuthToken } from "@/lib/api";
import { useLocaleSetter } from "@/contexts/LocaleContext";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    loading: true,
    user: null,
    org: null,
    isImpersonating: false,
    effectiveOrgId: null,
    // Phase B2 — server-resolved effective locale (user override → org default → en-GB).
    effectiveLocale: "en-GB",
  });
  // Phase B2 — push the locale resolved by the backend into LocaleContext so
  // every formatter call in the app respects the active org/user setting.
  const setLocale = useLocaleSetter();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      const effLoc = data.effective_locale || "en-GB";
      setState({
        loading: false,
        user: data.user,
        org: data.org,
        isImpersonating: data.is_impersonating,
        effectiveOrgId: data.effective_org_id,
        effectiveLocale: effLoc,
      });
      setLocale(effLoc);
      return data;
    } catch (e) {
      setState({ loading: false, user: null, org: null, isImpersonating: false, effectiveOrgId: null, effectiveLocale: "en-GB" });
      setLocale("en-GB");
      setAuthToken(null);
      return null;
    }
  }, [setLocale]);

  useEffect(() => {
    const token = localStorage.getItem("eb_access_token");
    if (token) {
      refresh();
    } else {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // v1.2 PR-B.3 — if MFA is on, the backend returns
    // {mfa_required:true, mfa_challenge_token, ...} instead of tokens. The
    // caller is responsible for calling `verifyMfa()` with the user's code
    // to complete login.
    if (data?.mfa_required) {
      return data;
    }
    setAuthToken(data.access_token);
    localStorage.setItem("eb_refresh_token", data.refresh_token);
    await refresh();
    return data;
  };

  const verifyMfa = async (challengeToken, code) => {
    const { data } = await api.post("/auth/login/mfa-verify", {
      mfa_challenge_token: challengeToken,
      code,
    });
    setAuthToken(data.access_token);
    localStorage.setItem("eb_refresh_token", data.refresh_token);
    await refresh();
    return data;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    setAuthToken(data.access_token);
    localStorage.setItem("eb_refresh_token", data.refresh_token);
    await refresh();
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setAuthToken(null);
    localStorage.removeItem("eb_refresh_token");
    setState({ loading: false, user: null, org: null, isImpersonating: false, effectiveOrgId: null, effectiveLocale: "en-GB" });
    setLocale("en-GB");
  };

  const impersonate = async (orgId) => {
    const { data } = await api.post("/super-admin/impersonate", { org_id: orgId });
    setAuthToken(data.access_token);
    localStorage.setItem("eb_refresh_token", data.refresh_token);
    await refresh();
  };

  const endImpersonation = async () => {
    const { data } = await api.post("/super-admin/end-impersonation");
    setAuthToken(data.access_token);
    await refresh();
  };

  return (
    <AuthContext.Provider
      value={{ ...state, refresh, login, verifyMfa, signup, logout, impersonate, endImpersonation }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
